import React, { useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import { Button, Input, Space, Table } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import "./browser_location_code_for_goods_receive.css";
import useSWR from "swr";
import axios from "axios";

const BrowserLocationCodeForGoodsReceive: React.FC<{ onSelectLocation: (locationCode: string) => void; warehouse?: string }> = ({ onSelectLocation, warehouse }) => { // ⚠️: terima warehouse prop
     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };

     interface DataType {
          id: number;
          code_rack: string;
          name_rack: string;
     }

     type DataIndex = keyof DataType;

     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState("");
     const searchInput = useRef<InputRef>(null);
     const fetcher = (url: string) => axios.get(url).then((res) => res.data);
     const { data: locationData = [], error: locationError } = useSWR<DataType[]>(
          warehouse
               ? `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-rack-locations?warehouse=${warehouse}`
               : `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-rack-locations`,
          fetcher,
     );

     if (locationError) {
          console.error("Error fetching location data:", locationError);
     }

     const handleSearch = (
          selectedKeys: string[],
          confirm: FilterDropdownProps["confirm"],
          dataIndex: DataIndex,
     ) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText("");
     };

     const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${String(dataIndex)
                              .replace(/([A-Z])/g, " $1")
                              .trim()}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters)}
                              size="small"
                              style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex);
                              }}>
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          onFilter: (value, record) =>
               record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
          filterDropdownProps: {
               onOpenChange(open) {
                    if (open) {
                         setTimeout(() => searchInput.current?.select(), 100);
                    }
               },
          },
          render: (text) =>
               searchedColumn === dataIndex ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     const columns: TableColumnsType<DataType> = [
          {
               title: "Location Code",
               dataIndex: "code_rack",
               key: "code_rack",
               width: "40%",
               ...getColumnSearchProps("code_rack"),
               sorter: (a, b) => a.code_rack.localeCompare(b.code_rack),
               sortDirections: ["ascend", "descend"],
          },
          {
               title: "Location Name",
               dataIndex: "name_rack",
               key: "name_rack",
               width: "60%",
               ...getColumnSearchProps("name_rack"),
               sorter: (a, b) => a.name_rack.localeCompare(b.name_rack),
               sortDirections: ["ascend", "descend"],
          },
     ];

     return (
          <div id="ieml" style={transformStyle}>
               <div className="gjs-grid-row" id="ij1v">
                    <div className="gjs-grid-column" id="isv6">
                         <Table<DataType>
                              columns={columns}
                              dataSource={locationData}
                              pagination={{ pageSize: 5 }}
                              rowKey="id"
                              onRow={(record) => {
                                   return {
                                        onDoubleClick: () => {
                                             onSelectLocation(record.code_rack);
                                        },
                                   };
                              }}
                         />
                    </div>
               </div>
          </div>
     );
};

export default BrowserLocationCodeForGoodsReceive;
